import React, { memo } from 'react';

import { Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';

import { useLanguage } from '../../hooks';
import { QuestionInterface } from '../../types/oldWebForms';

interface StyleSheet {
  [key: string]: React.CSSProperties;
}

// If the question that the skip depends on is one of the following types, limit the skip logic to only allow empty or not empty comparisons.
// These questions have slightly complex data structures that make it difficult to compare values.
const disableOption = (type: string) =>
  [
    'date',
    'static_text',
    'select_check_metadata',
    'select_text_metadata',
    'select_number_metadata',
    'select_option_metadata',
    'select_option_metadata_comments',
    'select_activity_period_time',
    'table',
    'barcode',
    'photo',
    'signature',
    'attachment',
  ].includes(type);

const SkipLogicComponent = memo((props: any) => {
  const {
    questionId,
    tableId,
    skipLogicQuestion,
    skipLogicType,
    skipLogicTarget,
    skipLogicValue,
    onSkipSettingChange,
    logicQuestions,
  } = props;
  const lang = useLanguage();

  const styles: StyleSheet = {
    mainContainer: {
      display: 'flex',
      flexDirection: 'column',
      marginBottom: 5,
      alignItems: 'center',
    },
    container: { display: 'flex', width: '50%' },
    text: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      width: 30,
    },
    tag: {
      fontSize: 12,
      height: 20,
    },
  };
  const logicIndex = logicQuestions.findIndex((q: QuestionInterface) => q.id === questionId);
  const skipLogicQuestionType = logicQuestions.find(
    (q: QuestionInterface) => q.id === skipLogicQuestion,
  )?.type;

  return (
    <div>
      <Divider style={{ margin: '5px 0px 15px' }} />
      <div style={styles.mainContainer}>
        <div style={styles.container}>
          <div style={styles.text}>{lang.form_builder.if}</div>
          <Select
            variant={'outlined'}
            value={skipLogicQuestion}
            onChange={(e) => {
              onSkipSettingChange(questionId, 'question', e.target.value, tableId);
            }}
            fullWidth
            margin='dense'
            style={{ fontSize: 12 }}
          >
            {logicQuestions &&
              logicQuestions
                .filter(
                  (q: QuestionInterface, idx: number) =>
                    idx < logicIndex && !['static_text', 'table'].includes(q.type),
                ) // Exclude static text since it doesn't have user input & table because it's too demanding to compare on change.
                .map((q: QuestionInterface, idx: number) => (
                  <MenuItem
                    style={styles.tag}
                    value={q.id}
                    key={`origin-question-${idx}`}
                  >
                    {q.name}
                  </MenuItem>
                ))}
          </Select>
        </div>
        <div style={styles.container}>
          <div style={styles.text}>{lang.form_builder.is}</div>
          <Select
            variant={'outlined'}
            value={skipLogicType}
            onChange={(e) => onSkipSettingChange(questionId, 'type', e.target.value, tableId)}
            fullWidth
            margin='dense'
            style={{ fontSize: 12 }}
          >
            <MenuItem
              style={styles.tag}
              value={'empty'}
            >
              {lang.form_builder.empty}
            </MenuItem>
            <MenuItem
              style={styles.tag}
              value={'not_empty'}
            >
              {lang.form_builder.notEmpty}
            </MenuItem>
            {!disableOption(skipLogicQuestionType) && (
              <MenuItem
                style={styles.tag}
                value={'equal'}
              >
                {lang.form_builder.equal}
              </MenuItem>
            )}
            {!disableOption(skipLogicQuestionType) && (
              <MenuItem
                style={styles.tag}
                value={'dif'}
              >
                {lang.form_builder.dif}
              </MenuItem>
            )}
            {!disableOption(skipLogicQuestionType) && (
              <MenuItem
                style={styles.tag}
                value={'contains'}
              >
                {lang.form_builder.contains}
              </MenuItem>
            )}
            {!disableOption(skipLogicQuestionType) && (
              <MenuItem
                style={styles.tag}
                value={'not_contains'}
              >
                {lang.form_builder.notContains}
              </MenuItem>
            )}
          </Select>
        </div>
        {!disableOption(skipLogicQuestionType) &&
          ['equal', 'dif', 'contains', 'not_contains'].includes(skipLogicType) && (
            <div style={styles.container}>
              <div style={styles.text}>{lang.form_builder.to}</div>
              <TextField
                variant={'outlined'}
                size='small'
                margin='dense'
                value={skipLogicValue}
                onChange={(e) => onSkipSettingChange(questionId, 'value', e.target.value, tableId)}
                fullWidth
                InputProps={{
                  style: {
                    fontSize: 12,
                  },
                }}
                style={{ margin: 0 }}
              />
            </div>
          )}
        <div style={styles.container}>
          <div style={styles.text}>{lang.form_builder.goTo}</div>
          <Tooltip
            placement='right'
            title={lang.form_builder.skipOnlyMobile}
          >
            <Select
              variant={'outlined'}
              value={skipLogicTarget || 'next'}
              onChange={(e) => onSkipSettingChange(questionId, 'target', e.target.value, tableId)}
              fullWidth
              margin='dense'
              style={{ fontSize: 12 }}
            >
              <MenuItem
                style={styles.tag}
                value={'next'}
              >
                {lang.form_builder.next}
              </MenuItem>
              {logicQuestions &&
                logicQuestions
                  .filter((q: QuestionInterface, idx: number) => idx > logicIndex)
                  .map((q: QuestionInterface, index: number) => (
                    <MenuItem
                      style={styles.tag}
                      value={q.id}
                      key={`target-question-${index}`}
                    >
                      {q.name}
                    </MenuItem>
                  ))}
              <MenuItem
                style={styles.tag}
                value={'end'}
              >
                {lang.form_builder.end}
              </MenuItem>
            </Select>
          </Tooltip>
        </div>
      </div>
    </div>
  );
});

SkipLogicComponent.displayName = 'SkipLogicComponent';
export { SkipLogicComponent, disableOption };
